//@flow
import React, {useState} from 'react';
import {Table, TableBody, TableContainer, makeStyles} from '@material-ui/core';

import TaxInvoiceTableHeader from './TaxInvoiceTableHeader';

import TaxInvoiceTableRow from './TaxInvoiceTableRow';
import {Pagination} from '@material-ui/lab';
import {
  useOrderTaxInvoiceOrderActions,
  useTaxInvoiceTablePagination,
  taxInvoiceCanCancelWithoutCreditNote,
} from '../../../utils';

import type {TaxInvoiceDocument} from '../../../../../entities';
import type {Theme} from '@material-ui/core';
import DownloadTaxInvoiceModal from '../../TaxInvoiceModal/DownloadTaxInvoiceModal';
import CreateCreditNote from '../../TaxInvoiceModal/CreateCreditNote';
import EditTaxInvoiceModal from '../../TaxInvoiceModal/EditTaxInvoiceModal';
import ConfirmModal from '../../../../../elements/Modals/ConfirmModal';
import {useToastActions} from '../../../../toast-alert/utils';

const useStyles = makeStyles((theme: Theme) => ({
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    padding: '1rem',
  },
}));

type Props = {
  documents: TaxInvoiceDocument[],
};

export default function TaxInvoiceTable({documents}: Props) {
  const classes = useStyles();
  const {showToast} = useToastActions();

  const {pagination, setPagination} = useTaxInvoiceTablePagination();
  const {cancelOrderTaxInvoice} = useOrderTaxInvoiceOrderActions();

  const [documentToCancel, setDocumentToCancel] = useState<
    TaxInvoiceDocument | typeof undefined
  >(undefined);

  const canCancelWithoutCreditNote =
    // $FlowFixMe
    documentToCancel && taxInvoiceCanCancelWithoutCreditNote(documentToCancel);

  const taxInvoiceToCancelLabel = documentToCancel?.documentType.label || '';

  return (
    <>
      <TableContainer>
        <Table
          className={classes.table}
          aria-label="Tabla de facturas"
          size="small"
        >
          <TaxInvoiceTableHeader />
          <TableBody>
            {documents.map(document => (
              <TaxInvoiceTableRow
                key={document.orderId}
                document={document}
                onDelete={() => {
                  setDocumentToCancel(document);
                }}
                onDownload={() => console.log('download')}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <footer className={classes.footer}>
        <Pagination
          count={
            pagination &&
            (pagination.size >= pagination.total
              ? 1
              : Math.ceil(pagination.total / pagination.size))
          }
          page={pagination.page + 1}
          className={classes.pagination}
          color="primary"
          onChange={(e, page) => {
            setPagination(page - 1);
          }}
        />
      </footer>

      <ConfirmModal
        message={
          canCancelWithoutCreditNote
            ? 'Se anulara la creaación del documento'
            : 'Se va a crear una Nota de Crédito para anular el documento'
        }
        open={!!documentToCancel}
        onCancel={() => setDocumentToCancel(undefined)}
        onClose={() => setDocumentToCancel(undefined)}
        onConfirm={() => {
          if (!documentToCancel) return;

          if (!canCancelWithoutCreditNote) {
            cancelOrderTaxInvoice(documentToCancel.orderTaxInvoiceId);
            setDocumentToCancel(undefined);
            return;
          }
          cancelOrderTaxInvoice(documentToCancel.orderTaxInvoiceId)
            .then(() => {
              showToast({
                title: canCancelWithoutCreditNote
                  ? 'Documento anulado'
                  : 'La nota de crédito está en cola',
                message: canCancelWithoutCreditNote
                  ? `El documento ${taxInvoiceToCancelLabel} ha sido anulado`
                  : `En el transcurso del día se generará la nota de crédito correspondiente a la ${taxInvoiceToCancelLabel}`,
                type: 'success',
                duration: 15000,
              });
            })
            .catch(() => {
              const title = canCancelWithoutCreditNote
                ? 'Error al anular el documento'
                : 'Error al generar la nota de crédito';
              const message = canCancelWithoutCreditNote
                ? `No se pudo anular el documento ${taxInvoiceToCancelLabel}`
                : `No se pudo cancelar la ${taxInvoiceToCancelLabel}, revisa que no haya otra nota de crédito pendiente`;
              showToast({
                title,
                message,
                type: 'danger',
                duration: 15000,
              });
            })
            .finally(() => {
              setDocumentToCancel(undefined);
            });
        }}
      />

      <DownloadTaxInvoiceModal
        open={false}
        onClose={() => console.log('close')}
      />
      <CreateCreditNote open={false} onClose={() => console.log('close')} />
      <EditTaxInvoiceModal open={false} onClose={() => console.log('close')} />
    </>
  );
}
